import { type ReactNode } from "react";

/* =====================================================================
   Render a legal document from docs/legal/*.md.

   THE POINT IS THAT THERE IS ONE COPY. These documents used to be JSX in
   this app, which meant the text a visitor read and the text the product
   asked people to ACCEPT were two artifacts that could disagree. The
   acceptance record stores a sha256 of the markdown; if this page kept
   its own prose, editing it would change what people read without
   changing what they are recorded as having agreed to — the precise
   failure the hash exists to catch.

   So the markdown is the source and this renders it. Small and
   deliberate rather than a markdown library: the input is ours,
   generated, and narrow (headings, paragraphs, lists, links, bold), and
   everything is emitted as TEXT through React, so no sanitiser is needed
   and no dependency is bought.
   ===================================================================== */

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) out.push(...bold(text.slice(last, m.index), `${key}-t${i}`));
    // Internal links resolve back onto the marketing site's own router, so a
    // same-origin href stays a normal navigation.
    const href = (m[2] ?? "").replace(/^https:\/\/weconsultrx\.com/, "");
    out.push(
      <a key={`${key}-a${i}`} href={href} className="text-forest-700 underline">
        {m[1] ?? ""}
      </a>
    );
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(...bold(text.slice(last), `${key}-t${i}`));
  return out;
}

function bold(text: string, key: string): ReactNode[] {
  return text.split(/\*\*/).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={`${key}-b${i}`}>{part}</strong>
    ) : (
      <span key={`${key}-s${i}`}>{part}</span>
    )
  );
}

/** Strip the H1 and the metadata table — LegalPage already renders the title
 *  and the effective date in its own header. */
export function LegalMarkdown({ markdown }: { markdown: string }) {
  const out: ReactNode[] = [];
  let list: string[] = [];

  const flush = (key: string) => {
    if (!list.length) return;
    out.push(
      <ul key={`ul-${key}`}>
        {list.map((li, i) => (
          <li key={i}>{inline(li, `${key}-${i}`)}</li>
        ))}
      </ul>
    );
    list = [];
  };

  markdown.split("\n").forEach((raw, idx) => {
    const line = raw.trimEnd();
    const key = String(idx);
    if (/^\s*\|/.test(line)) return; // metadata table
    if (line.startsWith("# ")) return; // title, rendered by LegalPage
    if (!line.trim()) return flush(key);
    if (line.startsWith("- ")) {
      list.push(line.slice(2));
      return;
    }
    flush(key);
    if (line.startsWith("### ")) out.push(<h3 key={key}>{inline(line.slice(4), key)}</h3>);
    else if (line.startsWith("## ")) out.push(<h2 key={key}>{inline(line.slice(3), key)}</h2>);
    else out.push(<p key={key}>{inline(line, key)}</p>);
  });
  flush("end");

  return <>{out}</>;
}

/** The declared **Effective** date, read from the document itself so the page
 *  header and the acceptance record can never disagree about the version. */
export function effectiveOf(markdown: string): string {
  return markdown.match(/\*\*Effective\*\*\s*\|\s*([0-9-]+)/)?.[1] ?? "";
}

/** The H1, for the same reason. */
export function titleOf(markdown: string): string {
  return markdown.match(/^#\s+(.+?)\s*$/m)?.[1] ?? "";
}
